import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAllSearchParams } from 'storfox-route-hooks'
import { prop } from 'ramda'
import { useDeepCompareEffect } from 'storfox-api-hooks'
import { useSnackbar } from 'storfox-snackbar'

import { escapeAtob, unescapeBtoa } from '~/utils'
import { DashboardLayout } from '~/components/Layouts'
import { useNotification } from '~/components/Notification'
import * as NAV from '~/constants/nav-titles'
import * as ROUTES from '~/constants/routes'
import {
  LOT_SHEETS_BULK_CREATE_COMPLETE,
  LOT_SHEETS_BULK_VALIDATE_COMPLETE,
  LOT_SHEETS_BULK_VALIDATE_STATUS
} from '~/constants/notification-topics'
import { useProgressCard } from '~/components/ProgressCard'
import useMessages from '~/hooks/useMessages'

import { useLotSheetsImport } from '../hooks'
import LotSheetsImport from '../components/LotSheetsImport/LotSheetsImport'

function LotSheetsImportContainer () {
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const messages = useMessages()
  const lotSheetImport = useLotSheetsImport()
  const { payload } = useAllSearchParams()
  const progressCard = useProgressCard()

  const validateStatus = useNotification(LOT_SHEETS_BULK_VALIDATE_STATUS)
  const validateComplete = useNotification(LOT_SHEETS_BULK_VALIDATE_COMPLETE)
  const uploadComplete = useNotification(LOT_SHEETS_BULK_CREATE_COMPLETE)

  const key = prop('key', escapeAtob(payload))

  useDeepCompareEffect(() => {
    if (key) {
      validateStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const validated = prop('totalValidated', result)
        const total = prop('total', result)

        progressCard.handleValidationChange({ current: validated, total })
      })

      validateComplete.subscribe(key, ({ payload }) => {
        navigate(
          `${ROUTES.LOT_SHEETS_IMPORT_PATH}?payload=${unescapeBtoa(payload)}`
        )

        validateComplete.setLoading(false)
      }, { replace: true })

      uploadComplete.subscribe(key, () => {
        navigate(ROUTES.LOT_SHEETS_LIST_PATH, { replace: true })
        uploadComplete.setLoading(false)
      })
    }

    return () => {
      validateStatus.unsubscribe()
      validateComplete.unsubscribe()
      uploadComplete.unsubscribe()
    }
  }, [key])

  const handleSubmit = () => {
    uploadComplete.setLoading(true)
    return lotSheetImport.create({ key })
      .then(() => snackbar({ message: messages.IMPORT_REQUEST_SUCCESS }))
  }

  const handleSubscribe = key => {
    snackbar({ message: messages.NOTIFICATION_WAIT })
    validateComplete.setLoading(true)
    navigate(`${ROUTES.LOT_SHEETS_IMPORT_PATH}?payload=${unescapeBtoa({ key })}`, { replace: true })
  }

  const handleFileRemove = () => {
    validateComplete.unsubscribe()
    uploadComplete.unsubscribe()
    validateStatus.unsubscribe()
    progressCard.resetValues()
  }

  const title = 'Import lot sheets'

  const isLoading = (
    lotSheetImport.isLoading ||
    validateComplete.isLoading ||
    uploadComplete.isLoading
  )

  const breadcrumbs = { title }

  return (
    <DashboardLayout
      title={title}
      activeNav={NAV.LOT_SHEETS}
      breadcrumbs={breadcrumbs}
    >
      <LotSheetsImport
        title={title}
        isLoading={isLoading}
        progressCard={progressCard}
        onSubscribe={handleSubscribe}
        onSubmit={handleSubmit}
        onFileRemove={handleFileRemove}
      />
    </DashboardLayout>
  )
}

export default LotSheetsImportContainer
